import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import FormattedTable from './FormattedTable';
import * as savedSearchesSelectors from './SavedSearchesTable/selectors';
import { prepareAdvancedSearchCriteriaToSave } from './SavedSearchesTable/utils';
import withTranslation from './withTranslation';
import { usePrevious } from '../custom-hooks';
import {
  getInitialApolloTableState,
  getLoadParameters,
  resolveFilters,
} from '../utils/savedSearchUtils';

const useStyles = makeStyles({
  emptyTableRoot: {
    background: 'white',
    border: '1px solid #dfdfdf',

    '& tfoot': {
      display: 'none',
    },
  },
  emptyRowRoot: {
    verticalAlign: 'middle',
    '& img': {
      display: 'none',
    },
  },
});

const SearchDataTable = ({
  disableFilters,
  dataKey,
  loadData,
  columns,
  initialSortedColumn,
  initialSortOrder,
  initialRowsPerPage,
  initialTableFilters,
  onDataChange = null,
  advancedSearchCriteria,
  defaultCriteria,
  customFilter,
  referencedColumns,
  dynamicParamCollection = {},
  onLoadData,
  translateString,
  showFilters,
  saveFilters,
  enableSaving,
  savedSearchFilters,
  initialPage,
  setClearAllTableData,
  shouldClearAllTableData,
  ...otherProps
}) => {
  const classes = useStyles();

  const { rows, size, rowsPerPageOptions, metadata, entityName, searchName } = otherProps;
  const [apolloTableState, setApolloTableState] = useState(
    getInitialApolloTableState(
      initialRowsPerPage,
      rowsPerPageOptions,
      initialSortedColumn,
      initialSortOrder,
      initialTableFilters,
      initialPage
    )
  );
  const [reset, setReset] = useState(false);

  const prevReset = usePrevious(reset);
  const prevShouldClearAllTableData = usePrevious(shouldClearAllTableData);

  const {
    filters: stateFilters,
    currentPage,
    currentPageSize,
    currentSortedColumn,
    currentSortOrder,
  } = apolloTableState;
  const dependencyFilters = JSON.stringify(stateFilters);
  const onChangeAndRefreshCustomFilter = JSON.stringify(customFilter);

  useEffect(() => {
    if (onDataChange) {
      onDataChange({ rows, size });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  useEffect(() => {
    // prevent table reset, when entityName or searchName is not a sting or
    // when shouldClearAllTableData state changed from "true" -> "false"
    if (
      typeof entityName !== 'string' ||
      typeof searchName !== 'string' ||
      (!shouldClearAllTableData && prevShouldClearAllTableData)
    ) {
      return;
    }

    // we need to reset the apollo table state to default, when entityName, searchName or shouldClearAllTableData has changed
    // also in this case we need to unmount the FormattedTable to reset it to default state
    setReset(true);
    setApolloTableState(
      getInitialApolloTableState(
        initialRowsPerPage,
        rowsPerPageOptions,
        initialSortedColumn,
        initialSortOrder,
        initialTableFilters,
        initialPage
      )
    );

    if (shouldClearAllTableData) {
      setClearAllTableData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, searchName, shouldClearAllTableData]);

  useEffect(() => {
    // prevent api call, when entity or searchName is not a sting or
    // when reset state changed from "true" -> "false"
    // or when shouldClearAllTableData is "true", because useEffect with reset will make this logic to be run again
    if (
      typeof entityName !== 'string' ||
      typeof searchName !== 'string' ||
      (!reset && prevReset) ||
      shouldClearAllTableData
    ) {
      return;
    }

    const loadDataParameters = getLoadParameters({
      customFilter,
      entityName,
      searchName,
      dataKey,
      currentPage,
      currentPageSize,
      referencedColumns,
      dynamicParamCollection,
      advancedSearchCriteria,
      stateFilters,
      currentSortedColumn,
      currentSortOrder,
      metadata,
    });

    if (reset) {
      setReset(false);
    }

    loadData(loadDataParameters);
    onLoadData(loadDataParameters);
    if (enableSaving) {
      const preparedAdvancedSearchCriteria = prepareAdvancedSearchCriteriaToSave(
        advancedSearchCriteria
      );
      saveFilters(searchName, {
        filters: stateFilters,
        sortedColumn: currentSortedColumn,
        sortOrder: currentSortOrder,
        advancedSearchCriteria: preparedAdvancedSearchCriteria,
        tablePage: currentPage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    currentPageSize,
    currentSortedColumn,
    currentSortOrder,
    dependencyFilters,
    reset,
    advancedSearchCriteria,
    onChangeAndRefreshCustomFilter,
  ]);

  useEffect(() => {
    //when advancedSearchCriteria changes we should always move user to a first table page
    //we should skip this only if shouldClearAllTableData is 'true', because useEffect with reset will handle this logic instead
    if (!shouldClearAllTableData) {
      setApolloTableState({ ...apolloTableState, currentPage: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedSearchCriteria, shouldClearAllTableData]);

  const handleChange = ({ rowsPerPage, sortedColumn, sortOrder, filters, page = 0 }) => {
    setApolloTableState({
      currentPage: page,
      currentPageSize: rowsPerPage,
      currentSortedColumn: sortedColumn || '',
      currentSortOrder: sortedColumn ? sortOrder : undefined,
      filters: resolveFilters(filters, columns),
    });
  };

  if (reset) {
    return null;
  }

  return (
    <FormattedTable
      columns={columns}
      page={currentPage}
      rowsPerPage={currentPageSize}
      sortedColumn={currentSortedColumn ?? ''}
      sortOrder={currentSortOrder}
      onChange={handleChange}
      showFilters={showFilters}
      classes={{
        root: size === 0 ? classes.emptyTableRoot : null,
      }}
      emptyProps={{
        classes: {
          root: classes.emptyRowRoot,
        },
        text: translateString('No data to display'),
      }}
      savedSearchFilters={savedSearchFilters}
      customFilter={customFilter}
      {...otherProps}
    />
  );
};

const StyledSearchDataTable = SearchDataTable;

const mapStateToProps = (state, ownProps) => {
  return {
    defaultCriteria: savedSearchesSelectors.getDefaultCriteria(state, ownProps),
  };
};

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation(StyledSearchDataTable));
